import { useEffect, useMemo, useState } from 'react'
import { client } from '../lib/api'
import { placeholderGradient } from '../lib/ui'
import { ProfileSheet } from './ProfileSheet'
import { LIVE_POLL_MS, useLiveRefresh } from '../lib/live'
import type { PublicProfile } from '../../shared/types'

export type LikedProfile = PublicProfile & { mutual?: boolean; likedBack?: boolean; viewedAt?: string }

type LikesTab = 'viewed-me' | 'favorited-me' | 'favorites'
type SortKey = 'recent' | 'name'

type PhotoMeta = { url: string | null; count: number }

function sortProfiles<T extends PublicProfile>(list: T[], sort: SortKey): T[] {
  const copy = [...list]
  if (sort === 'name') {
    copy.sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' }))
  }
  return copy
}

function FavoriteCard({
  profile,
  photo,
  favorited,
  onOpen,
  onMessage,
  onToggleFavorite,
}: {
  profile: LikedProfile
  photo: PhotoMeta | undefined
  favorited: boolean
  onOpen: () => void
  onMessage: () => void
  onToggleFavorite: () => void
}) {
  const photoUrl = photo?.url ?? null
  const photoCount = photo?.count ?? 0

  return (
    <article className="favorite-card">
      <button type="button" className="favorite-card-photo" onClick={onOpen}>
        {photoUrl ? (
          <img src={photoUrl} alt="" className="favorite-card-bg" loading="lazy" />
        ) : (
          <div className="favorite-card-bg favorite-card-placeholder" style={{ background: placeholderGradient(profile.accentColor) }} aria-hidden>
            {profile.displayName.slice(0, 1).toUpperCase()}
          </div>
        )}
        {photoCount > 0 && (
          <span className="favorite-photo-count" aria-label={`${photoCount} photos`}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2zM8.5 13.5l2.5 3 3.5-4.5 4.5 6H5l3.5-4.5z" />
            </svg>
            {photoCount}
          </span>
        )}
        <div className="favorite-card-overlay">
          <strong>
            {profile.online && <span className="online-dot" aria-label="Online" />}
            {profile.displayName}
            {profile.verified && (
              <span className="verified-badge" aria-label="Verified">
                ✓
              </span>
            )}
          </strong>
          <span>
            {profile.age != null ? profile.age : '—'}
            {profile.city ? ` · ${profile.city}` : ''}
            {profile.mutual ? ' · Match' : ''}
          </span>
        </div>
      </button>
      <div className="favorite-card-actions">
        <button type="button" className="favorite-action" aria-label={`Message ${profile.displayName}`} onClick={onMessage}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
        <button
          type="button"
          className={`favorite-action favorite-action-heart${favorited ? ' is-active' : ''}`}
          aria-label={favorited ? `Remove ${profile.displayName} from favorites` : `Favorite ${profile.displayName}`}
          onClick={onToggleFavorite}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill={favorited ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
          </svg>
        </button>
      </div>
    </article>
  )
}

function dedupeById<T extends { id: string }>(list: T[]): T[] {
  const seen = new Set<string>()
  const out: T[] = []
  for (const item of list) {
    if (seen.has(item.id)) continue
    seen.add(item.id)
    out.push(item)
  }
  return out
}

export function Likes({
  onOpenChat,
  onMatch,
  notify,
}: {
  onOpenChat: (p: LikedProfile) => void
  onMatch?: (p: LikedProfile) => void
  notify: (msg: string) => void
}) {
  const [tab, setTab] = useState<LikesTab>('favorited-me')
  const [sort, setSort] = useState<SortKey>('recent')
  const [viewedMe, setViewedMe] = useState<LikedProfile[] | null>(null)
  const [favoritedMe, setFavoritedMe] = useState<LikedProfile[] | null>(null)
  const [favorites, setFavorites] = useState<LikedProfile[] | null>(null)
  const [photoMap, setPhotoMap] = useState<Record<string, PhotoMeta>>({})
  const [sheetId, setSheetId] = useState<string | null>(null)

  const load = async (which: LikesTab) => {
    try {
      if (which === 'viewed-me') {
        const r = await client.profileViewers()
        const viewers = dedupeById(r.viewers as LikedProfile[])
        setViewedMe(viewers)
        return viewers
      }
      if (which === 'favorited-me') {
        const r = await client.likedMe()
        const profiles = dedupeById(r.profiles as LikedProfile[])
        setFavoritedMe(profiles)
        return profiles
      }
      const r = await client.likes()
      const profiles = dedupeById(r.profiles as LikedProfile[])
      setFavorites(profiles)
      return profiles
    } catch (e) {
      notify((e as Error).message)
      if (which === 'viewed-me') setViewedMe([])
      else if (which === 'favorited-me') setFavoritedMe([])
      else setFavorites([])
      return []
    }
  }

  useEffect(() => {
    void load(tab)
  }, [tab]) // eslint-disable-line react-hooks/exhaustive-deps

  useLiveRefresh(() => {
    void load(tab)
  }, LIVE_POLL_MS, true)

  const rawList = tab === 'viewed-me' ? viewedMe : tab === 'favorited-me' ? favoritedMe : favorites
  const list = useMemo(() => (rawList ? sortProfiles(rawList, sort) : null), [rawList, sort])

  useEffect(() => {
    if (!list?.length) return
    void (async () => {
      const next: Record<string, PhotoMeta> = { ...photoMap }
      for (const p of list) {
        if (next[p.id]) continue
        try {
          const d = await client.profileDetail(p.id)
          const publicPhotos = d.photos.filter((ph) => ph.kind === 'public' && ph.urlPath)
          next[p.id] = {
            url: publicPhotos[0]?.urlPath ?? null,
            count: publicPhotos.length,
          }
        } catch {
          next[p.id] = { url: null, count: 0 }
        }
      }
      setPhotoMap(next)
    })()
  }, [list]) // eslint-disable-line react-hooks/exhaustive-deps

  const sheetProfile = list?.find((p) => p.id === sheetId) ?? null

  const removeFromList = (id: string) => {
    if (tab === 'viewed-me') setViewedMe((prev) => (prev ? prev.filter((p) => p.id !== id) : prev))
    else if (tab === 'favorited-me') setFavoritedMe((prev) => (prev ? prev.filter((p) => p.id !== id) : prev))
    else setFavorites((prev) => (prev ? prev.filter((p) => p.id !== id) : prev))
  }

  const toggleFavorite = async (profile: LikedProfile) => {
    if (tab === 'favorites' || profile.likedBack || profile.mutual) {
      try {
        await client.unlike(profile.id)
        notify('Removed from favorites')
        removeFromList(profile.id)
      } catch (e) {
        notify((e as Error).message)
      }
      return
    }
    try {
      const res = await client.like(profile.id)
      if (res.matched) {
        onMatch?.(profile)
        notify("You're connected!")
        removeFromList(profile.id)
      } else {
        notify('Like sent')
        void load(tab)
      }
    } catch (e) {
      notify((e as Error).message)
    }
  }

  const emptyCopy =
    tab === 'viewed-me'
      ? { icon: '👀', title: 'No profile views yet', body: 'When someone views your profile, they appear here.' }
      : tab === 'favorited-me'
        ? { icon: '💫', title: 'No one new yet', body: 'When someone favorites you, they appear here.' }
        : { icon: '♡', title: 'No favorites yet', body: 'Tap the heart on Discover when someone catches your eye.' }

  return (
    <div className="panel-page interests-page">
      <span className="eyebrow">Interest</span>
      <h1>Favorites</h1>

      <div className="panel-tabs" role="tablist">
        <button className={tab === 'viewed-me' ? 'active' : ''} onClick={() => setTab('viewed-me')} type="button" role="tab">
          Viewed me
        </button>
        <button className={tab === 'favorited-me' ? 'active' : ''} onClick={() => setTab('favorited-me')} type="button" role="tab">
          Favorited me
        </button>
        <button className={tab === 'favorites' ? 'active' : ''} onClick={() => setTab('favorites')} type="button" role="tab">
          Favorites
        </button>
      </div>

      {list && list.length > 0 && (
        <label className="interests-sort">
          <span>Sort by</span>
          <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
            <option value="recent">{tab === 'viewed-me' ? 'When viewed' : 'When favorited'}</option>
            <option value="name">Name</option>
          </select>
        </label>
      )}

      {list === null ? (
        <div className="loading-center">
          <div className="spinner" />
        </div>
      ) : list.length === 0 ? (
        <div className="empty">
          <div>{emptyCopy.icon}</div>
          <h2>{emptyCopy.title}</h2>
          <p>{emptyCopy.body}</p>
        </div>
      ) : (
        <div className="favorite-grid">
          {list.map((p) => (
            <FavoriteCard
              key={p.id}
              profile={p}
              photo={photoMap[p.id]}
              favorited={tab === 'favorites' || !!p.likedBack || !!p.mutual}
              onOpen={() => setSheetId(p.id)}
              onMessage={() => onOpenChat(p)}
              onToggleFavorite={() => void toggleFavorite(p)}
            />
          ))}
        </div>
      )}

      <ProfileSheet
        profileId={sheetId}
        onClose={() => setSheetId(null)}
        notify={notify}
        variant="fullscreen"
        showActions
        likedByViewer={tab === 'favorites'}
        likedBack={!!sheetProfile?.likedBack || !!sheetProfile?.mutual}
        onMessage={(profile) => {
          setSheetId(null)
          onOpenChat(profile as LikedProfile)
        }}
        onLike={async (profile) => {
          try {
            if (tab === 'favorites') {
              notify('Already in your favorites')
              return
            }
            const res = await client.like(profile.id)
            if (res.matched) {
              onMatch?.(profile as LikedProfile)
              notify("You're connected!")
            } else {
              notify('Like sent')
            }
            setSheetId(null)
            void load(tab)
          } catch (e) {
            notify((e as Error).message)
          }
        }}
        onUnlike={
          tab === 'favorites'
            ? async (id) => {
                try {
                  await client.unlike(id)
                  notify('Removed from favorites')
                  setSheetId(null)
                  removeFromList(id)
                } catch (e) {
                  notify((e as Error).message)
                }
              }
            : undefined
        }
        onPass={
          tab === 'favorited-me'
            ? async (id) => {
                try {
                  await client.pass(id)
                  notify('Passed')
                  setSheetId(null)
                  removeFromList(id)
                } catch (e) {
                  notify((e as Error).message)
                }
              }
            : undefined
        }
      />
    </div>
  )
}
